import { createValidationPayError, changePayData } from "./pay-data-actions";
import {
  createFatalError,
  createValidationProfileError,
} from "./errors-actions";
import { transformSyntheticEvent } from "../../utils/helper-functions/transform-syntetic-event";
import PraxisService from "../../context-components/service";
import { triggerAdvanceCashierEvent } from "../../utils/custom-cashier-events/trigger-advance-cashier-event";
import { sanitizeNonLatinCharacters } from "../../utils/helper-functions/sanitize-non-latin-characters";
import { sendOpenSearchLogs } from "utils/helper-functions/send-open-search-logs.ts";
import { RecoverySettingsModel } from "utils/models/RecoverySettings.Model.ts";
import { getDeviceData } from "../../utils/helper-functions/get-device-data";
import { cardToObSettings } from "./cardToObSettingsAction";
import { isTest } from "../../variables/host";
import { ResponseStatus } from "../../variables/response-status";
import { isPanoramicTheme } from "@pano-project/variables/is-panoramic-2-0-theme";
import { previewBonusPromocodes } from "@pano-project/variables/preview-bonus-promocodes"
import {convertFromMinorUnits, mathCeilDecimal} from "../../utils/round-functions/math-ceil-decimal";
import { round } from "../../utils/round-functions/round";
import { findCurrentFee, calculateAmountWithPercent } from "../../utils/fee-functions/calculate-comission";
import { convertAmountBetweenCurrencies } from "@pano-project/utils/formatFees";

const service = new PraxisService();
const {
    postPayGateway,
    getUser,
    getTransactionStatus,
    saveData,
    getGateways,
    getGatewayDetails,
    partialApprovedResult,
    manageRetryRequest,
    postErrors,
    getAvailableGateways,
    getOBTMethods,
    manageTemplate
} = service;

export const setEnableSessionEvents = (payload) => {
  return {
    type: "SET_ENABLE_SESSION_EVENTS",
    payload,
  };
};

export const createSessionId = (payload) => {
  return {
    type: "SESSION_ID_INIT",
    payload,
  };
};

export const tokenInit = (payload) => {
  return {
    type: "TOKEN_INIT",
    payload,
  };
};

export const createVirtualSettings = (payload) => {
  return {
    type: "VIRTUAL_SETTINGS",
    payload,
  };
};

export const changeInitTheme = (payload) => {
  return {
    type: "INIT_THEME",
    payload,
  };
};

export const updateGateways = (payload) => {
  return {
    type: "UPDATE_GATEWAYS",
    payload,
  };
};

export const updateProcessingGateways = (payload) => {
  return {
    type: "UPDATE_PROCESSING_GATEWAYS",
    payload,
  };
};

export const setPendingTransactions = (payload) => {
  return {
    type: "SET_PENDING_TRANSACTIONS",
    payload,
  };
};

export const setWithdrawalRequests = (payload) => {
  return {
    type: "SET_WITHDRAWAL_REQUESTS",
    payload,
  };
};

export const fetchWithdrawalRequests = () => (dispatch) => {
  return new Promise((resolve) => {
    service
      .serverGetTransactions()
      .then(({ data } = {}) => {
        const withdrawalRequestsData = data?.withdrawal_requests || [];
        dispatch(setWithdrawalRequests(withdrawalRequestsData));
        resolve(withdrawalRequestsData);
      })
      .catch(() => {
        dispatch(setWithdrawalRequests([]));
        resolve([]);
      });
  });
};

export const createResultOfPay = (payload) => {
  return {
    type: "CREATE_RESULT_OF_PAY",
    payload,
  };
};

const isOnlyTransactionType = (payload) => {
  const keys = Object.keys(payload);
  return keys.length === 1 && keys[0] === 'transaction_type';
}

export const changeProfile = (payload) => {
  if (!isOnlyTransactionType(payload)) {
    //Sanitize the customer fields based in the restrict_non_latin_characters flag
    payload = sanitizeNonLatinCharacters(payload, ["customer"]);
  }
  return {
    type: "UPDATE_PROFILE",
    payload,
  };
};

export const createMainFocus = (payload) => {
  payload = transformSyntheticEvent(payload);
  return {
    type: "MAIN_FOCUS",
    payload,
  };
};

export const updateGatewayDetails = (payload) => {
  //Sanitize the gateway templates based in the restrict_non_latin_characters flag
  if (payload && typeof payload === 'object' && payload.templates) {
    payload = sanitizeNonLatinCharacters(payload, ["templates"]);
  }
  return {
    type: "CREATE_CURRENT_GATEWAY",
    payload,
  };
};

export const updateGatewayInfoFromAmountPage = (payload) => {
  return {
    type: "CREATE_CURRENT_GATEWAY_FROM_AMOUNT_PAGE",
    payload,
  };
};

export const updateAdditionalFields = (additional_fields, psp_bank_id) => {
    return  additional_fields.map(field => {
        if (field.name === 'bank_id') {
            return { ...field, default_value: psp_bank_id };
        }
        return field;
    });
}

export const createCurrentGateway = (data, selectionSource = 'unknown') => (dispatch, getState) => {
    return new Promise((resolve) => {
        const card_type_name = data.card_type_name ?? null;
        const { psp_bank_id, recently_used_template, recently_used } = data;
        const state = getState();
        const layout = state?.initTheme?.theme_editor_settings?.customization_settings?.layout;
        const isAmountFirst = layout === 'amount-first';

        if (isAmountFirst) {
            dispatch({
                type: "SET_GATEWAY_SELECTION_SOURCE",
                payload: selectionSource,
            });
        }
        
        getGatewayDetails(data)
          .then(({data}) => {
            if (data.status === ResponseStatus.OK) {
                let updatedAdditionalFields;
                if (psp_bank_id) {
                    updatedAdditionalFields = updateAdditionalFields(data.additional_fields, psp_bank_id);
                }

                if (isAmountFirst) {
                    const recentlyUsedTokens = (recently_used_template || []).map(t => t?.token).filter(Boolean);
                    if (recentlyUsedTokens.length === 0) {
                        data.templates = {};
                    } else {
                        const templatesArray = data.templates || [];
                        const matchedTemplates = templatesArray.filter(t =>
                            t?.token && recentlyUsedTokens.includes(t.token)
                        );
                        data.templates = matchedTemplates.reduce((acc, template) => {
                            if (template.label) {
                                acc[template.label] = template;
                            }
                            return acc;
                        }, {});
                    }
                }

                if (window.cashierAdditionalFieldsModifier) {
                  const dataToModify = { ...data, additional_fields: updatedAdditionalFields || data.additional_fields };
                  window.cashierAdditionalFieldsModifier(dataToModify).then((el) => {
                      if (el.status === ResponseStatus.OK) data.additional_fields = el.additional_fields;

                        dispatch(updateGatewayDetails({
                            ...data,
                            card_type_name,
                            ...(isPanoramicTheme && {
                              recently_used_template,
                              recently_used
                          }),
                        }))
                        dispatch(setLimitSubmit(false));
                    }).catch((error) => {
                        sendOpenSearchLogs({
                            event_group: "cashier_additional_fields_modifier",
                            error_data: {
                                error: {
                                    message: "cashierAdditionalFieldsModifier error in createCurrentGateway",
                                    stack: error
                                }
                            }
                        });
                        dispatch(updateGatewayDetails({
                            ...data,
                            card_type_name,
                            ...(updatedAdditionalFields && {
                                additional_fields: updatedAdditionalFields
                            })
                        }))
                        dispatch(setLimitSubmit(false));
                    });
                } else {
                    dispatch(updateGatewayDetails({
                        ...data,
                        card_type_name,
                        ...(isPanoramicTheme && {
                            recently_used_template,
                            recently_used
                        }),
                        ...(updatedAdditionalFields && {
                            additional_fields: updatedAdditionalFields
                        })
                    }))
                };

              dispatch(setLimitSubmit(false));
            }
            if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
            if (data.status === ResponseStatus.SERVER_ERROR) dispatch(createFatalError(data.error_details));

            sendOpenSearchLogs({
              event_group: "cashier_events",
              event_name: "payment_method_selected",
              event_data: {
                ...data,
                templates: Object.keys(data?.templates || {})
              },
              event_context: {
                response_code: data.status,
              },
            });

        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: "cashier_get_gateway_details",
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;

        if (data.status === ResponseStatus.OK) {
          if (isAmountFirst) {
            const recentlyUsedTokens = (recently_used_template || []).map(t => t?.token).filter(Boolean);
            if (recentlyUsedTokens.length === 0) {
              data.templates = {};
            } else {
              const templatesArray = data.templates || [];
              const matchedTemplates = templatesArray.filter(t =>
                  t?.token && recentlyUsedTokens.includes(t.token)
              );
              data.templates = matchedTemplates.reduce((acc, template) => {
                if (template.label) {
                  acc[template.label] = template;
                }
                return acc;
              }, {});
            }
          }
          dispatch(updateGatewayDetails({
            ...data,
            ...(isPanoramicTheme && {
              recently_used_template,
              recently_used
            })
          }));
          dispatch(setLimitSubmit(false));
        }
        if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
        if (data.status === ResponseStatus.SERVER_ERROR) dispatch(createFatalError(data.error_details));
        if (!data.status) dispatch(createFatalError());
        resolve(data);
      });
  });
};

export const saveLocation = (payload) => {
  return {
    type: "SAVE-LOCATION",
    payload,
  };
};

export const changeBaseAmount = (payload) => {
  return {
    type: "CHANGE_BASE_AMOUNT",
    payload,
  };
};

export const changeAmountFirstStep = (payload) => {
  return {
    type: "CHANGE_AMOUNT_FIRST_STEP",
    payload,
  };
};

const dispatchAllMethodsInMaintenance = (gateways, dispatch) => {
  const allMethodsInMaintenance = gateways?.length > 0 && gateways.every((g) => g.maintenance?.enabled === 1);
  dispatch({ type: "SET_ADD_FIELDS_INFO", payload: { allMethodsInMaintenance } });
};

const filterMaintenanceGateways = (gateways = []) => {
  if (isPanoramicTheme) return gateways;
  return gateways.filter(gw => gw?.maintenance?.enabled !== 1);
};

const cashierGatewayValidator = (data, dispatch) => {
  if (window.cashierGatewayValidator) {
    return window.cashierGatewayValidator(data).then((el) => {
      if (el.status === ResponseStatus.OK) data.gateways = el.gateways;
      data.gateways = filterMaintenanceGateways(data.gateways);
      dispatch(updateGateways(data));
      dispatchAllMethodsInMaintenance(data.gateways, dispatch);
      return { data };
    });
  }
  data.gateways = filterMaintenanceGateways(data.gateways);
  dispatch(updateGateways(data));
  dispatchAllMethodsInMaintenance(data.gateways, dispatch);
  return { data };
};

export const resetCardToObSettings = () => (dispatch) => {
    dispatch(cardToObSettings({
        show_obt_banner: false,
        obt_gateway: null,
        card_gateway: null,
        psp_bank_id: null,
        show_back_to_card: false
     }));
};

export const checkObtCurrencyAndAmountSupport = ({obt_gateway} = {}) => (dispatch, getState) => {
    const {
        payData,
        cardToObSettings: obtSettings,
        gatewaysList,
        processingGatewaysList,
        initTheme: { theme_editor_settings: { customization_settings: { layout, currency_display_mode } = {} } = {} } = {},
        profileInfo: { base_currency } = {}
    } = getState();

    const selectedCurrency = currency_display_mode === 'base_currency' ? base_currency : payData?.currency;
    const amount = payData?.amount;
    const obtGateway = obt_gateway || obtSettings?.obt_gateway;
    const processing_currencies = obtGateway?.payment_information?.processing_currencies;

    const supportedCurrency = processing_currencies?.find((gatewayCurrency) => {
        const { currency, min_amount, max_amount } = gatewayCurrency;
        return currency === selectedCurrency && (!amount || (amount >= Number(min_amount) && amount <= Number(max_amount)));
    });

    if (supportedCurrency) {
        dispatch(cardToObSettings({
            show_obt_banner: true
        }));
    } else {
        dispatch(cardToObSettings({
            show_obt_banner: false
        }));
    }
};

export const fetchOBTMethods = (currency, options = {}) => (dispatch, getState) => {
    return new Promise((resolve) => {
        const { shouldApplyResult } = options;
        const checkCurrency = typeof currency === 'string' ? currency : currency?.currency;
        getOBTMethods(checkCurrency)
            .then((res = {}) => {
                let data;

                if(isTest) {
                    data = res.data;
                } else {
                    data = res;
                }

                const { bank_supported, psp_bank_id } = data;
                const obtGateway = data.gateway_list[0];

                if (typeof shouldApplyResult === 'function' && !shouldApplyResult()) {
                    dispatch(resetCardToObSettings());
                    resolve(data);
                    return;
                }

                if (bank_supported === 1 && psp_bank_id && obtGateway) {
                    dispatch(cardToObSettings({
                        obt_gateway: { ...obtGateway, option_id: data.gateway_id },
                        psp_bank_id: data.psp_bank_id
                    }));

                    dispatch(checkObtCurrencyAndAmountSupport({obt_gateway: { ...obtGateway, option_id: data.gateway_id }}));
                } else {
                    dispatch(resetCardToObSettings());
                };

                resolve(data);
            }).catch(() => {
                dispatch(resetCardToObSettings());
                resolve(null);
            });
    })
};

export const fetchGateways = (ob_decline_recovery = 0, retry_with_apm = 0, first_gateway_details = false) => (dispatch, getState) => {
    const state = getState();
    const {
      profileInfo: { transaction_type, base_amount, base_currency }, initTheme: {cashier_theme}
    } = state;
    const last_used_method_first = state?.initTheme?.theme_editor_settings?.customization_settings?.last_used_method_first;
    const layout = state?.initTheme?.theme_editor_settings?.customization_settings?.layout;

    const obRecovery = layout === 'amount-first' ? (ob_decline_recovery ? 1 : 0) : ob_decline_recovery;
    const apmRecovery = layout === 'amount-first' ? (retry_with_apm ? 1 : 0) : retry_with_apm;

    return getGateways(transaction_type, obRecovery, apmRecovery, last_used_method_first, first_gateway_details)
        .then((res = {}) => {
            const { data } = res;
            const { initTheme: { theme_editor_settings: { customization_settings: { layout } = {} } = {} } = {} } = state;
            const isAmountFirst = layout === 'amount-first';
            const isObtSupported = data?.gateways?.some(gateway => gateway.is_obt_supported) ? 1 : 0;
            dispatch(cardToObSettings({ 
                is_obt_supported: isObtSupported,
                is_card_to_ob_available: isAmountFirst ? !!isObtSupported : false
            }));
            

        sendOpenSearchLogs({
          event_group: "cashier_initialization",
          event_name: "open_cashier_state",
          event_data: {
            amount: base_amount,
            currency: base_currency,
            available_payment_methods: data.gateways?.map(gate => gate?.payment_method_key),
          },
        });

        if (data.status === ResponseStatus.OK) return cashierGatewayValidator(data, dispatch);
        if (data.status === ResponseStatus.FAIL && apmRecovery === 1) {
          return {is_retry_apm_cancel: 1}
        }
        if (data.status === ResponseStatus.FAIL && apmRecovery === 0) dispatch(createResultOfPay(data));
        if (data.status === ResponseStatus.SERVER_ERROR) dispatch(createFatalError(data.error_details));
        if (!data) dispatch(createFatalError());
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: "cashier_get_gateways",
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;

        if (data.status === ResponseStatus.OK) return cashierGatewayValidator(data, dispatch);
        if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
        if (data.status === ResponseStatus.SERVER_ERROR) dispatch(createFatalError(data.error_details));
        if (!data.status) dispatch(createFatalError());
      });
  };

export const fetchProcessingGateways = (data) => (dispatch, getState) => {
  return new Promise((resolve) => {
    return getAvailableGateways({ ...data })
      .then((res) => {
        const { data = {} } = res;
        const { status } = data;

        sendOpenSearchLogs({
          event_name: "get_avaible_gateways",
          event_group: "cashier_events",
          event_data: {
            gateway_list: data.available_gateways,
          },
        });

        if (status === ResponseStatus.OK) {
          dispatch(updateProcessingGateways(data));
          const { initTheme: { theme_editor_settings: { customization_settings: { layout } = {} } = {} } = {} } = getState();
          const isAmountFirst = layout === 'amount-first';
          if (isAmountFirst) {
            const isObtSupported = data?.available_gateways?.some(gateway => gateway.is_obt_supported) ? 1 : 0;
            dispatch(cardToObSettings({ 
                is_obt_supported: isObtSupported,
                is_card_to_ob_available: isAmountFirst ? !!isObtSupported : false
            }));
          }
        } else {
          if (status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
          if (status === ResponseStatus.SERVER_ERROR) dispatch(createFatalError(data.error_details));
        }
        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: "cashier_get_available_gateways",
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {}, data: { status } = {} } = response;

        if (status === ResponseStatus.OK) {
          dispatch(updateProcessingGateways(data));
          const { initTheme: { theme_editor_settings: { customization_settings: { layout } = {} } = {} } = {} } = getState();
          const isAmountFirst = layout === 'amount-first';
          if (isAmountFirst) {
            const isObtSupported = data?.available_gateways?.some(gateway => gateway.is_obt_supported) ? 1 : 0;
            dispatch(cardToObSettings({ 
                is_obt_supported: isObtSupported,
                is_card_to_ob_available: isAmountFirst ? !!isObtSupported : false
            }));
          }
        } else {
          if (status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
          if (status === ResponseStatus.SERVER_ERROR) dispatch(createFatalError(data.error_details));
          if (!status) {
            postErrors({
              error: {
                stack: "getAvailableGateways catch",
                message: JSON.stringify(response),
              },
            });
            dispatch(createFatalError());
          }
        }
        resolve(data);
      });
  });
};

export const handleResultResponse = (data, dispatch) => {
  const { status } = data;
  switch (status) {
    case ResponseStatus.OK:
      dispatch(createResultOfPay(data));
      break;
    case ResponseStatus.FAIL:
      dispatch(createResultOfPay(data));
      break;
    case ResponseStatus.SERVER_ERROR:
      dispatch(createFatalError(data.error_details));
      break;
    case ResponseStatus.ERROR_VALIDATION:
      dispatch(createValidationPayError(data.error_details));
      break;
    default:
      dispatch(createFatalError());
  }
};

const delayPayWithGateway = () => {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      getUser(1).then(({ data }) => {
        const { processing_in_progress } = data;
        if (!processing_in_progress) {
          clearInterval(interval);
          resolve({ data });
        }
      });
    }, 10000);
  });
};

const updateRecoverySettingsAfterPayment = ({ dispatch, recoveryOptions }) => {
  const updatedRecoverySettings = {
    ...(recoveryOptions.ob_decline_recovery && {
      ob_decline_recovery: 0,
    }),
    ...(recoveryOptions.retry_with_apm && {
      retry_with_apm: 0,
    }),
  };

  RecoverySettingsModel.updateRecoverySettings({
    dispatch,
    recoverySettings: updatedRecoverySettings,
  });
};

export const payWithGateway =
  (data, isQuickDeposit = false) =>
  (dispatch, getState) => {
    const {
      payData: { promocode },
      currentGateway: { option_id, option_type },
      gatewaysList: { is_encrypted_input_names = 0 },
      virtualSettings,
      profileInfo: { remove_customer_data },
      initTheme: { theme_editor_settings: { customization_settings: { layout } = {} } = {} }
    } = getState();

    const recoverySettings = RecoverySettingsModel.getRecoverySettings();

    const apm_decline_recovery = layout === 'amount-first' ? (recoverySettings.retry_with_apm ? 1 : 0) : recoverySettings.retry_with_apm;
    const ob_decline_recovery = layout === 'amount-first' ? (recoverySettings.ob_decline_recovery ? 1 : 0) : recoverySettings.ob_decline_recovery;

    const params = {
      ...(promocode && {
        promocode: promocode
      }),
      option_id,
      option_type,
      is_encrypted_input_names,
      remove_customer_data,
      ...virtualSettings,
      ...data,
    };

    sendOpenSearchLogs({
      event_group: "cashier_events",
      event_name: "cashier_submit",
      event_data: {
        option_id: params.option_id,
        option_type: params.option_type,
        is_encrypted_input_names: params.is_encrypted_input_names,
        remove_customer_data: params.remove_customer_data,
        override_limits: params.override_limits,
        payment_information: data.transaction,
        device_data: getDeviceData(),
      },
      event_context: {
        response_code: data.status,
      },
    });

    if (data?.additional_fields) {
      const updatedAdditionalFields = {};

      Object.entries(data.additional_fields).forEach(([key, value]) => {
        if (typeof value !== "string") {
          updatedAdditionalFields[key] = value;
          return;
        }

          // If remove_customer_data is true and the key is "email" or "phone,"
          // then just assign the value and skip the check for "**".
          if (remove_customer_data && ["email", "phone"].includes(key)) {
              updatedAdditionalFields[key] = value;
              return;
          }

          // Otherwise, assign null if the string contains "**"; otherwise, keep the original value.
          updatedAdditionalFields[key] = value.includes("**") ? null : value;
      });

      params.additional_fields = updatedAdditionalFields;
    }

    //tell the BE that this transaction is quick deposit
    if (isQuickDeposit) {
      params["is_quick_deposit"] = 1;
    }

    const recoveryOptions = {
      ob_decline_recovery,
      retry_with_apm: apm_decline_recovery,
    };

    return new Promise((resolve) => {
      return postPayGateway({
        ...params,
        ...(apm_decline_recovery && { apm_decline_recovery }),
        ...(ob_decline_recovery && { ob_decline_recovery }),
      })
        .then(({ data } = {}) => {
          const { status, processing_in_progress } = data;

          if ((ob_decline_recovery || apm_decline_recovery)) {
            updateRecoverySettingsAfterPayment({ dispatch, recoveryOptions });
          }

          if (status === ResponseStatus.OK) {
            const { transaction } = data;
            triggerAdvanceCashierEvent({
              event_type: "transaction_attempted",
              transaction: {
                amount: transaction?.amount,
                currency: transaction?.currency,
                charge_amount: transaction?.charge_amount,
                charge_currency: transaction?.charge_currency,
                payment_method: transaction?.payment_method,
                payment_processor: transaction?.payment_processor,
                trace_id: transaction?.trace_id,
                card_number: transaction?.card_number,
              },
            });
          }

          if ([0, 2].includes(status) && processing_in_progress) {
            return delayPayWithGateway().then(({ data }) => {
              handleResultResponse(data, dispatch);
              resolve(data);
            });
          }
          handleResultResponse(data, dispatch);

          resolve(data);
        })
        .catch((res = {}) => {
          sendOpenSearchLogs({
            event_group: "cashier_post_pay_gateway",
            error_data: res,
          });

          if ((ob_decline_recovery || apm_decline_recovery)) {
            updateRecoverySettingsAfterPayment({ dispatch, recoveryOptions });
          }

          const { response = {} } = res;
          const { data = {}, data: { status, processing_in_progress } = {} } =
            response;

          if ([0, 2].includes(status) && processing_in_progress) {
            return delayPayWithGateway().then(({ data }) => {
              handleResultResponse(data, dispatch);
              resolve(data);
            });
          }
          handleResultResponse(data, dispatch);
          resolve(data);
        });
    });
  };

export const payWithPartiallyApproved = (data) => (dispatch) => {
  return new Promise((resolve) => {
    return partialApprovedResult(data)
      .then(({ data } = {}) => {
        handleResultResponse(data, dispatch);
        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: "cashier_pay_with_partially_approved",
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;

        handleResultResponse(data, dispatch);
        resolve(data);
      });
  });
};

export const payWithManageRetryRequest = (data) => (dispatch, getState) => {
  const { payData, initTheme, profileInfo, currentGateway } = getState();
  const isCurrencyConversion = payData.currency && payData.base_currency && payData.currency !== payData.base_currency;

  const retryData = { 
    ...data,
    ...payData,
    amount: isCurrencyConversion ? Number(payData.totalCharge) : Number(payData.amount)
  };

  return new Promise((resolve) => {
    return manageRetryRequest(retryData)
      .then(({ data } = {}) => {
        handleResultResponse(data, dispatch);
        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: "cashier_pay_with_manage_retry_request",
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;
        handleResultResponse(data, dispatch);
        resolve(data);
      });
  });
};

export const cancelWithManageRetryRequest = (data) => (dispatch, getState) => {
  const { resultOfPay } = getState();
  return new Promise((resolve) => {
    return manageRetryRequest({
      ...data, action: 'cancel',
      original_trace_id: `${resultOfPay?.transaction?.trace_id}`
    })
      .then(({ data } = {}) => {
        handleResultResponse(data, dispatch);

        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: "cashier_cancel_with_manage_retry_request",
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;
        handleResultResponse(data, dispatch);
        resolve(data);
      })
  })
}

export const getCustomerData =
  (callback = 0) =>
  (dispatch, getState) => {
    return new Promise((resolve) => {
      return getUser(callback)
        .then((res = {}) => {
          const { data } = res;

          if (callback && data.redirect_url) data.redirect_url = null;
          if (data.status === ResponseStatus.OK) {
            if (window.cashierUpdateCustomer)
              try {
                window.cashierUpdateCustomer(data.customer);
              } catch (e) {
                sendOpenSearchLogs({
                  event_group: "cashier_update_customer",
                  error_data: {
                    error: {
                      message: "getUser error",
                      stack: e,
                    },
                  },
                });
              }
            const { initTheme: { cashier_preview_mode } } = getState();
            if(isPanoramicTheme && cashier_preview_mode && !data?.promocode_options?.length) {
              dispatch(changeProfile({ ...data, promocode_options: previewBonusPromocodes() }));
            } else {
                let updatedData = { ...data };

                if (updatedData?.promocode_options?.length > 0) {
                    updatedData.promocode_options = updatedData.promocode_options.map((promo) => {
                        const currencies = promo.constraints?.processing_currencies || {};

                        const reformattedCurrencies = Object.fromEntries(
                            Object.entries(currencies).map(([currency, { min_amount, max_amount }]) => {
                                return [
                                    currency,
                                    {
                                        min_amount: convertFromMinorUnits(min_amount, currency),
                                        max_amount: convertFromMinorUnits(max_amount, currency),
                                    },
                                ];
                            })
                        );

                        return {
                            ...promo,
                            constraints: {
                                ...promo.constraints,
                                processing_currencies: reformattedCurrencies,
                            },
                        };
                    });
                }

                dispatch(changeProfile(updatedData));
            }
          }
          if (data.status === ResponseStatus.FAIL) {
            const { resultOfPay: currentResult } = getState();
            const currentTraceId = currentResult?.transaction?.trace_id;
            const incomingTraceId = data?.transaction?.trace_id;
            const currentStatus = currentResult?.transaction?.status;
            const isCurrentPending = ['pending', 'pending_async', 'initialized'].includes(currentStatus);
            const isStaleResult = currentTraceId && incomingTraceId
              && currentTraceId !== incomingTraceId && isCurrentPending;
            const layout = getState()?.initTheme?.theme_editor_settings?.customization_settings?.layout;
            const shouldNormalizeRetryWithApm = callback && layout === 'amount-first' && (data?.retry_with_apm || data?.ob_decline_recovery);

            if (!isStaleResult) {
              if (shouldNormalizeRetryWithApm) {
                dispatch(createResultOfPay({
                  ...data,
                  retry_with_apm: data?.retry_with_apm ? 1 : 0,
                  ob_decline_recovery: data?.ob_decline_recovery ? 1 : 0
                }));
              } else {
                dispatch(createResultOfPay(data));
              }
            }
          }
          if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
          if (data.status === ResponseStatus.ERROR_VALIDATION)
            dispatch(createValidationProfileError(data.error_details));
          if (data.status === ResponseStatus.SERVER_ERROR)
            dispatch(createFatalError(data.error_details));
          if (data.status === ResponseStatus.ERROR_PROFILE_AMOUNT) dispatch(changeProfile(data));

          sendOpenSearchLogs({
            event_group: "cashier_initialization",
            event_name: "get_customer_data",
            event_data: data,
            event_context: {
              http_status_code: res.status,
              response_code: data.status,
              response_message: res.statusText,
            },
          });

          resolve(data);
        })
        .catch((res = {}) => {
          sendOpenSearchLogs({
            event_group: "cashier_get_user",
            error_data: res,
          });

          const { response = {} } = res;
          const { data = {} } = response;
          if (data.status === ResponseStatus.OK) {
            if (window.cashierUpdateCustomer)
              try {
                window.cashierUpdateCustomer(data.customer);
              } catch {}
            dispatch(changeProfile(data));
          }
          if (data.status === ResponseStatus.FAIL) {
            const { resultOfPay: currentResult } = getState();
            const currentTraceId = currentResult?.transaction?.trace_id;
            const incomingTraceId = data?.transaction?.trace_id;
            const currentStatus = currentResult?.transaction?.status;
            const isCurrentPending = ['pending', 'pending_async', 'initialized'].includes(currentStatus);
            const isStaleResult = currentTraceId && incomingTraceId
              && currentTraceId !== incomingTraceId && isCurrentPending;
            const layout = getState()?.initTheme?.theme_editor_settings?.customization_settings?.layout;
            const shouldNormalizeRetryWithApm = callback && layout === 'amount-first' && (data?.retry_with_apm || data?.ob_decline_recovery);

            if (!isStaleResult) {
              if (shouldNormalizeRetryWithApm) {
                dispatch(createResultOfPay({
                  ...data,
                  retry_with_apm: data?.retry_with_apm ? 1 : 0,
                  ob_decline_recovery: data?.ob_decline_recovery ? 1 : 0
                }));
              } else {
                dispatch(createResultOfPay(data));
              }
            }
          }
          if (data.status === ResponseStatus.ERROR_VALIDATION)
            dispatch(createValidationProfileError(data.error_details));
          if (data.status === ResponseStatus.SERVER_ERROR)
            dispatch(createFatalError(data.error_details));
          if (data.status === ResponseStatus.ERROR_PROFILE_AMOUNT) dispatch(changeProfile(data));
          if (!data.status) dispatch(createFatalError());
          resolve(data);
        });
    });
  };

export const pollTransactionStatus =
    (trace_id) => (dispatch, getState) => {
        return new Promise((resolve) => {
            return getTransactionStatus(trace_id ? {trace_id} : {})
                .then((res = {}) => {
                    const { data = {} } = res;
                    const nextPayload = data.resultOfPay || data;

                    if (nextPayload && nextPayload.transaction ) {
                        const current = getState().resultOfPay || {};
                        const mergedPayLoad = {
                            ...current,
                            ...nextPayload,
                            transaction: {
                                ...current.transaction,
                                ...nextPayload.transaction
                            },
                        };
                        dispatch(createResultOfPay(mergedPayLoad));
                    }
                    resolve(nextPayload);
                })
                .catch((res = {}) => {
                    sendOpenSearchLogs({
                        event_group: "cashier_poll_transaction_status",
                        error_data: res,
                    });
                    resolve({});
                });

        });
    };

export const saveDataInfo = (amount) => (dispatch) => {
  return new Promise((resolve) => {
    saveData({ ...amount })
      .then(({ data }) => {
        if (data.status === ResponseStatus.ERROR_VALIDATION)
          dispatch(
            createValidationPayError(data?.error_details?.payment_information),
          );
        if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
        if (data.status === ResponseStatus.OK) resolve(data);
        resolve(data);
      })
      .catch((res = {}) => {
        sendOpenSearchLogs({
          event_group: "cashier_save_data_info",
          error_data: res,
        });

        const { response = {} } = res;
        const { data = {} } = response;
        if (data.status === ResponseStatus.ERROR_VALIDATION)
          dispatch(
            createValidationPayError(data?.error_details?.payment_information),
          );
        if (data.status === ResponseStatus.FAIL) dispatch(createResultOfPay(data));
        if (data.status === ResponseStatus.OK) resolve(data);
        if (!data.status) dispatch(createFatalError());
        resolve(data);
      });
  });
};

export const setLimitSubmit = (payload) => {
  return {
    type: "SET_LIMIT_SUBMIT",
    payload,
  };
};

export const setLimitError = (payload) => {
  return {
    type: "SET_LIMIT_ERROR",
    payload,
  };
};

export const setPortalWrappers = (payload) => {
  return {
    type: "SET_PORTAL_WRAPPERS",
    payload,
  }
}
export const setForceValidationDisable = (payload) => {
  return {
    type: "SET_FORCE_VALIDATION_DISABLE",
    payload,
  };
};
export const sendManageTemplateRequest = ({ template_token, template_type, action }) => (dispatch) => {
  return new Promise((resolve) => {
    service
      .manageTemplate({ template_token, template_type, action })
      .then(({ data } = {}) => {
        const manageTemplateResult = data;
        resolve(manageTemplateResult);
      })
      .catch(() => {
        resolve();
      });
  });
};

export const setIsManualAmountChange = (payload) => {
  return {
    type: "SET_IS_MANUAL_AMOUNT_CHANGE",
    payload,
  };
};
